import { getFormTabErrorCounts } from '../../../../../../lib/formTabErrors';
import { useState } from 'react';
import { Braces, ListChecks, UsersRound } from 'lucide-react';
import { Controller, useFormContext, useFormState } from 'react-hook-form';
import {
  DashboardPageFormBody,
  DashboardPageFormField,
  DashboardPageFormTab,
  DashboardPageFormTabList,
  DashboardPageFormTabPanel,
  DashboardPageFormTabs,
} from '@shigo-ui/dashboard-ui';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shigo-ui/ui';

import { ChoiceCardField } from '../../../../../../components/forms/ChoiceCardField';
import { showRequestError } from '../../../../../../lib/requestErrorToast';
import { dashboardT } from '../../../../../../lib/messages';
import { downloadBlob } from '../../../../../../lib/downloadBlob';
import { NetworkAvailableFeature } from '../../../../../auth/networkFeatures';
import { useNetworkFeatures } from '../../../../../auth/useNetworkFeatures';
import {
  useTargetingBuilderClient,
  useStandaloneTargetingClient,
} from '../../../../../targeting/builder/hooks';
import { TargetingBuilderEditorTab } from '../../../../../targeting/builder/model';
import { demandConnectionTargetingConfig } from '../../../../../targeting/config';
import { CustomTargetingForm } from '../../../../../targeting/components/CustomTargetingForm';
import { LiverampTargetingForm } from '../../../../../targeting/components/LiverampTargetingForm';
import { TargetingForm } from '../../../../../targeting/components/TargetingForm';
import {
  IntegrationEditorTab,
  IntegrationSchainLogicMode,
  IncompleteSchainHandling,
} from '../../../model';
import type { IntegrationFormValue } from '../../../integrationFormSchema';

const incompleteSchainLabels = {
  [IncompleteSchainHandling.DropRequest]: 'integrations.incompleteSchainHandling.dropRequest',
  [IncompleteSchainHandling.Incomplete]: 'integrations.incompleteSchainHandling.incomplete',
  [IncompleteSchainHandling.Complete]: 'integrations.incompleteSchainHandling.complete',
} as const;

const schainModes = {
  [IntegrationSchainLogicMode.DirectOnly]: {
    label: 'integrations.schainMode.direct_only',
    description: 'integrations.schainMode.direct_onlyDescription',
  },
  [IntegrationSchainLogicMode.ResellerPriority]: {
    label: 'integrations.schainMode.reseller_priority',
    description: 'integrations.schainMode.reseller_priorityDescription',
  },
  [IntegrationSchainLogicMode.DirectPriority]: {
    label: 'integrations.schainMode.direct_priority',
    description: 'integrations.schainMode.direct_priorityDescription',
  },
  [IntegrationSchainLogicMode.ResellerOnly]: {
    label: 'integrations.schainMode.reseller_only',
    description: 'integrations.schainMode.reseller_onlyDescription',
  },
} as const;

export function IntegrationAdvancedFields({ tab }: { tab: IntegrationEditorTab }) {
  const { control } = useFormContext<IntegrationFormValue>();
  if (tab === IntegrationEditorTab.Targeting) {
    return <IntegrationTargetingFields />;
  }
  if (tab === IntegrationEditorTab.Schain) {
    return (
      <DashboardPageFormBody
        width="lg"
        title={dashboardT('integrations.tabs.schain')}
        description={dashboardT('integrations.schainDescription')}
      >
        <Controller
          control={control}
          name="schainLogicMode"
          render={({ field }) => (
            <ChoiceCardField
              label={dashboardT('integrations.schainMode')}
              description={
                <>
                  <p>{dashboardT('integrations.hints.schainMode')}</p>
                  <p>{dashboardT('integrations.hints.schainOwner')}</p>
                </>
              }
              name={field.name}
              value={field.value}
              onValueChange={field.onChange}
              onBlur={field.onBlur}
              options={Object.values(IntegrationSchainLogicMode).map((mode) => ({
                value: mode,
                label: dashboardT(schainModes[mode].label),
                description: dashboardT(schainModes[mode].description),
              }))}
            />
          )}
        />
        <Controller
          control={control}
          name="incompleteSchainHandling"
          render={({ field, fieldState }) => (
            <DashboardPageFormField
              label={dashboardT('integrations.incompleteSchainHandling')}
              required
              error={fieldState.error?.message}
            >
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger
                  ref={field.ref}
                  onBlur={field.onBlur}
                  aria-label={dashboardT('integrations.incompleteSchainHandling')}
                  aria-invalid={Boolean(fieldState.error)}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(IncompleteSchainHandling).map((value) => (
                    <SelectItem key={value} value={value}>
                      {dashboardT(incompleteSchainLabels[value])}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </DashboardPageFormField>
          )}
        />
      </DashboardPageFormBody>
    );
  }
  return null;
}

function IntegrationTargetingFields() {
  const { control, setValue, watch } = useFormContext<IntegrationFormValue>();
  const { errors, submitCount } = useFormState({ control });
  const { isFeatureAllowed } = useNetworkFeatures();
  const liveRampAllowed = isFeatureAllowed(NetworkAvailableFeature.LiveRamp);
  const targetingClient = useStandaloneTargetingClient();
  const builderClient = useTargetingBuilderClient();
  const [activeTab, setActiveTab] = useState(TargetingBuilderEditorTab.Targeting);
  const targetingErrors = errors.targeting as Record<string, { message?: string }> | undefined;
  const targetingId = watch('targetingId');
  const bundlesCount = watch('bundlesCount');
  const ipsCount = watch('ipsCount');
  const pendingBundleCsv = watch('pendingBundleCsv');
  const pendingIpCsv = watch('pendingIpCsv');

  const tabErrors = getFormTabErrorCounts(errors, {
    [TargetingBuilderEditorTab.Targeting]: [
      'targeting',
      'targetingId',
      'targetingName',
      'bundlesCount',
      'ipsCount',
      'pendingBundleCsv',
      'pendingIpCsv',
    ],
    [TargetingBuilderEditorTab.Custom]: ['customTargeting'],
    [TargetingBuilderEditorTab.Liveramp]: ['liverampTargeting'],
  });

  return (
    <DashboardPageFormTabs
      value={activeTab}
      onValueChange={(value) => {
        setActiveTab(value as TargetingBuilderEditorTab);
      }}
    >
      <DashboardPageFormTabList>
        <DashboardPageFormTab
          value={TargetingBuilderEditorTab.Targeting}
          errorCount={tabErrors[TargetingBuilderEditorTab.Targeting]}
          errorLabel={(count) => dashboardT('common.validationErrors', { count })}
          icon={<ListChecks className="h-4 w-4" aria-hidden="true" />}
        >
          {dashboardT('targetingBuilder.targetingTab')}
        </DashboardPageFormTab>
        <DashboardPageFormTab
          value={TargetingBuilderEditorTab.Custom}
          errorCount={tabErrors[TargetingBuilderEditorTab.Custom]}
          errorLabel={(count) => dashboardT('common.validationErrors', { count })}
          icon={<Braces className="h-4 w-4" aria-hidden="true" />}
        >
          {dashboardT('targetingBuilder.customTab')}
        </DashboardPageFormTab>
        {liveRampAllowed ? (
          <DashboardPageFormTab
            value={TargetingBuilderEditorTab.Liveramp}
            errorCount={tabErrors[TargetingBuilderEditorTab.Liveramp]}
            errorLabel={(count) => dashboardT('common.validationErrors', { count })}
            icon={<UsersRound className="h-4 w-4" aria-hidden="true" />}
          >
            {dashboardT('targetingBuilder.liverampTab')}
          </DashboardPageFormTab>
        ) : null}
      </DashboardPageFormTabList>
      <DashboardPageFormTabPanel value={TargetingBuilderEditorTab.Targeting}>
        <Controller
          control={control}
          name="targeting"
          render={({ field }) => (
            <TargetingForm
              config={demandConnectionTargetingConfig}
              client={targetingClient}
              liveRampAllowed={liveRampAllowed}
              csvControls={{
                bundles: {
                  targetingId,
                  count: bundlesCount,
                  file: pendingBundleCsv,
                  onFileChange: (file, rowCount) => {
                    setValue('pendingBundleCsv', file);
                    setValue('bundlesCount', rowCount);
                  },
                  onExport: () => {
                    void targetingClient
                      .exportBundlesCsv(targetingId)
                      .then((blob) => {
                        downloadBlob(blob, 'targeting-bundles.csv');
                      })
                      .catch((error: unknown) => {
                        showRequestError(error, dashboardT('integrations.targetingCsvExportError'));
                      });
                  },
                },
                ips: {
                  targetingId,
                  count: ipsCount,
                  file: pendingIpCsv,
                  onFileChange: (file, rowCount) => {
                    setValue('pendingIpCsv', file);
                    setValue('ipsCount', rowCount);
                  },
                  onExport: () => {
                    void targetingClient
                      .exportIpsCsv(targetingId)
                      .then((blob) => {
                        downloadBlob(blob, 'targeting-ips.csv');
                      })
                      .catch((error: unknown) => {
                        showRequestError(error, dashboardT('integrations.targetingCsvExportError'));
                      });
                  },
                },
              }}
              errors={Object.fromEntries(
                Object.entries(targetingErrors ?? {}).map(([key, error]) => [
                  key,
                  error.message ?? '',
                ]),
              )}
              submitted={submitCount > 0}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </DashboardPageFormTabPanel>
      <DashboardPageFormTabPanel value={TargetingBuilderEditorTab.Custom}>
        <CustomTargetingForm />
      </DashboardPageFormTabPanel>
      {liveRampAllowed ? (
        <DashboardPageFormTabPanel value={TargetingBuilderEditorTab.Liveramp}>
          <LiverampTargetingForm client={builderClient} />
        </DashboardPageFormTabPanel>
      ) : null}
    </DashboardPageFormTabs>
  );
}
